import { useState, useEffect, useMemo } from 'react'
import { useSocket } from '../../context/SocketContext'
import { FiSearch, FiFilter, FiGrid, FiList } from 'react-icons/fi'
import AgentCard from '../../components/AgentCard/AgentCard'
import './Agents.css'

const Agents = () => {
  const { agents, isConnected } = useSocket()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [viewMode, setViewMode] = useState('grid')
  const [selectedAgent, setSelectedAgent] = useState(null)

  const filteredAgents = useMemo(() => {
    return agents.filter(agent => {
      const matchesSearch = agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           agent.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           agent.agent_id?.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesStatus = statusFilter === 'all' || agent.status === statusFilter
      
      return matchesSearch && matchesStatus
    })
  }, [agents, searchTerm, statusFilter])

  const statusCounts = useMemo(() => {
    const counts = { all: agents.length, online: 0, busy: 0, offline: 0, error: 0, maintenance: 0 }
    agents.forEach(a => { if (counts[a.status] !== undefined) counts[a.status]++ })
    return counts
  }, [agents])

  return (
    <div className="agents-page">
      <div className="agents-header">
        <div>
          <h1>Agent Network</h1>
          <p className="agents-subtitle">
            {isConnected 
              ? `${statusCounts.online} of ${agents.length} agents online`
              : 'Connecting to agent network...'}
          </p>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="status-tabs">
        {[
          { key: 'all', label: 'All', count: statusCounts.all },
          { key: 'online', label: 'Online', count: statusCounts.online },
          { key: 'busy', label: 'Busy', count: statusCounts.busy },
          { key: 'offline', label: 'Offline', count: statusCounts.offline },
          { key: 'error', label: 'Error', count: statusCounts.error }
        ].map(tab => (
          <button
            key={tab.key}
            className={`status-tab ${statusFilter === tab.key ? 'active' : ''} tab-${tab.key}`}
            onClick={() => setStatusFilter(tab.key)}
          >
            <span className="tab-label">{tab.label}</span>
            <span className="tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Search and view controls */}
      <div className="agents-toolbar">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search agents by name, role, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="view-controls">
          <button
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <FiGrid />
          </button>
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <FiList />
          </button>
        </div>
      </div>

      {/* Agent grid/list */}
      <div className={`agents-container ${viewMode === 'grid' ? 'agent-grid' : 'agent-list'}`}>
        {filteredAgents.length === 0 ? (
          <div className="no-agents">
            <div className="no-agents-icon">🔍</div>
            <h3>No agents found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          filteredAgents.map(agent => (
            <AgentCard 
              key={agent.agent_id} 
              agent={agent} 
              onClick={() => setSelectedAgent(agent)}
            />
          ))
        )}
      </div>

      {/* Quick stats */}
      <div className="agents-stats">
        <div className="stat-item">
          <span className="stat-label">Total Agents</span>
          <span className="stat-value">{agents.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Online</span>
          <span className="stat-value stat-online">{statusCounts.online}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Busy</span>
          <span className="stat-value stat-busy">{statusCounts.busy}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Offline</span>
          <span className="stat-value stat-offline">{statusCounts.offline}</span>
        </div>
      </div>
    </div>
  )
}

export default Agents
